<template>
  <div class="h-100 d-flex flex-column align-center justify-space-around fit-content mx-auto ga-5">
    <ConnectingLine
      v-if="linkedReadModels.length"
      :startElementId="`domain-event-${system.index}-${event.index}`"
      :endElementId="`updated-projection-${system.index}-${event.index}`"
      :refreshKey="refreshKey"
      direction="auto"
      offsetParentId="domain-model"
    />
    <div
      v-if="linkedReadModels.length"
      :id="`updated-projection-${system.index}-${event.index}`"
      :class="{'model-card-border': linkedReadModels.length > 1}"
      :style="{ '--border-color': linkedReadModels.length ? cardTypeColor(queryCardType) : '' }"
      class="w-100 d-flex flex-column ga-3"
    >
      <div
        v-for="rm in linkedReadModels"
        :key="rm.id"
        class="model-container"
        :style="{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }"
      >
        <div
          class="card-item"
          :style="{ 'border-color': cardTypeColor(queryCardType) }"
        >
          <div class="d-flex align-center mb-1">
            <v-icon size="14" class="mr-1" :style="{ color: cardTypeColor(queryCardType) }">mdi-eye-outline</v-icon>
            <span class="schema-name" :style="{ color: cardTypeColor(queryCardType) }">{{ rm.name }}</span>
          </div>

          <div v-if="getLinkedEntity(rm)" class="mb-1">
            <v-chip density="compact" size="small" class="fit-content">
              <v-icon start size="small">mdi-database</v-icon>
              {{ getLinkedEntity(rm).name }}
            </v-chip>
          </div>

          <div v-if="showEntities && (rm.fields ?? []).length" class="py-1">
            <div
              v-for="(field, fieldIndex) in rm.fields"
              :key="fieldIndex"
              class="field-name d-flex align-center"
            >
              <span class="flex-1-0">{{ field.name }}</span>
              <v-chip
                v-if="field.relatedEntityId && getRelatedEntity(field)"
                density="compact"
                size="small"
                class="px-2 ml-1"
              >
                {{ field.cardinality === 'one-to-many'
                  ? `[ ${getRelatedEntity(field)?.name} ]`
                  : `${getRelatedEntity(field)?.name}` }}
              </v-chip>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useStore } from 'vuex'
import ConnectingLine from '@/components/ConnectingLine.vue'
import { diagramHelpers } from '@/mixins/diagramHelpers'
import useDomainModel from '@/composables/useDomainModel'
import { SchemaType } from '@/constants/schema'
import useReadModelDomainEvents from '@/composables/useReadModelDomainEvents'

const { system, event, showEntities, refreshKey } = defineProps({
  system: Object,
  event: Object,
  showEntities: Boolean,
  refreshKey: Number
})

const { cardTypeColor } = useDomainModel()
const { getReadModelsForDomainEvent } = useReadModelDomainEvents()
const store = useStore()
const workflow = computed(() => store.state.workflow.workflow)
const cardTypes = computed(() => store.state.cardTypes.cardTypes)
const queryCardType = computed(() => diagramHelpers.methods.getQueryCardType(cardTypes.value))
const domainEventCard = computed(() => system.domainEventCards?.[event.index] || null)

const linkedDomainEventSchema = computed(() => {
  if (!domainEventCard.value?.schemaId) {
    return null
  }

  return (workflow.value?.schemas ?? []).find(
    (s) => s.id === domainEventCard.value.schemaId && s.type === SchemaType.DOMAIN_EVENT
  )
})

const linkedReadModels = computed(() => {
  return getReadModelsForDomainEvent(linkedDomainEventSchema.value)
})

const getLinkedEntity = (schema) => {
  if (!schema.entityId) {
    return null
  }

  return (workflow.value?.schemas ?? []).find((s) => s.id === schema.entityId && s.type === SchemaType.ENTITY)
}

const getRelatedEntity = (field) => {
  if (!field.relatedEntityId) {
    return null
  }

  return (workflow.value?.schemas ?? []).find((s) => s.id === field.relatedEntityId)
}
</script>

<style scoped>
.schema-name {
  font-size: 14px;
  font-weight: 500;
}

.model-card-border {
  border: 1px dashed var(--border-color);
  border-radius: 8px;
  padding: 8px;
}

.fit-content {
  width: fit-content;
}

.field-name {
  line-height: 1;
  height: 22px;
  padding: 2px;
}
</style>
